import {
  createBrowserRouter,
  createMemoryRouter,
  type InitialEntry,
  type RouteObject,
} from 'react-router';

import type { AuthenticationApi } from './auth/api';
import { AuthProvider } from './auth/AuthProvider';
import { AuthenticationGateway } from './auth/AuthenticationGateway';
import type { AuthenticationMode } from './auth/authState';
import { UserManagementPage } from './auth/UserManagementPage';
export { featureRoutes } from './navigation';
export type { FeatureRoute } from './navigation';
import { DashboardPage } from './pages/HomePage';
import { CurveLibraryReviewPage } from './pages/CurveLibraryReviewPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RouteErrorPage } from './pages/RouteErrorPage';
import { CatalogPage, CurvesPage, ExportsPage, FleetPage, NetworkPage, OptimizationPage, ProjectsPage, ResultsPage, ScenariosPage, SettingsPage, SimulationPage } from './pages/WorkflowPages';
import { PrivacyPage, PublicLandingPage, TermsPage } from './public/PublicPages';
import { PublicShell } from './public/PublicShell';

export interface AppRouterOptions {
  authenticationApi?: AuthenticationApi | undefined;
  initialAuthenticationMode?:
    | Extract<AuthenticationMode, 'local'>
    | undefined;
  initialPasswordResetToken?: string | null | undefined;
}

interface AuthenticationBoundaryProps extends AppRouterOptions {
  resetTokenHolder: { current: string | null };
}

function AuthenticationBoundary({
  authenticationApi,
  initialAuthenticationMode,
  resetTokenHolder,
}: AuthenticationBoundaryProps) {
  return (
    <AuthProvider
      api={authenticationApi}
      initialMode={initialAuthenticationMode}
      initialPasswordResetToken={resetTokenHolder.current}
      onPasswordResetConsumed={() => {
        resetTokenHolder.current = null;
      }}
    >
      <AuthenticationGateway />
    </AuthProvider>
  );
}

export function createAppRoutes(options: AppRouterOptions = {}): RouteObject[] {
  const resetTokenHolder = {
    current: options.initialPasswordResetToken ?? null,
  };

  return [
    {
      path: '/',
      element: <PublicShell />,
      errorElement: <RouteErrorPage />,
      children: [
        { index: true, element: <PublicLandingPage /> },
        { path: 'privacy', element: <PrivacyPage /> },
        { path: 'terms', element: <TermsPage /> },
      ],
    },
    {
      element: (
        <AuthenticationBoundary
          {...options}
          resetTokenHolder={resetTokenHolder}
        />
      ),
      errorElement: <RouteErrorPage returnTo="/app" />,
      children: [
        { path: 'login' },
        { path: 'reset-password' },
        { path: 'app', element: <DashboardPage /> },
        { path: 'users', element: <UserManagementPage /> },
        { path: 'admin/curve-library', element: <CurveLibraryReviewPage /> },
        { path: 'settings', element: <SettingsPage /> },
        { path: 'catalog', element: <CatalogPage /> },
        { path: 'fleet', element: <FleetPage /> },
        { path: 'curves', element: <CurvesPage /> },
        { path: 'projects', element: <ProjectsPage /> },
        { path: 'network', element: <NetworkPage /> },
        { path: 'scenarios', element: <ScenariosPage /> },
        { path: 'simulation', element: <SimulationPage /> },
        { path: 'optimization', element: <OptimizationPage /> },
        { path: 'results', element: <ResultsPage /> },
        { path: 'exports', element: <ExportsPage /> },
        { path: '*', element: <NotFoundPage returnTo="/app" /> },
      ],
    },
  ];
}

export const appRoutes = createAppRoutes();

export function createAppBrowserRouter(options: AppRouterOptions = {}) {
  return createBrowserRouter(createAppRoutes(options));
}

export function createAppMemoryRouter(
  initialEntries: InitialEntry[] = ['/'],
  options: AppRouterOptions = {},
) {
  return createMemoryRouter(createAppRoutes(options), {
    initialEntries,
  });
}
